import * as React from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export interface LoadingButtonProps extends ButtonProps {
  loading?: boolean;
  loadingText?: string;
  successState?: boolean;
  errorState?: boolean;
}

export const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({ 
    children, 
    loading = false, 
    loadingText, 
    successState = false,
    errorState = false,
    disabled,
    className,
    ...props 
  }, ref) => {
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [showError, setShowError] = React.useState(false);

    React.useEffect(() => {
      if (successState && !loading) {
        setShowSuccess(true);
        const timer = setTimeout(() => setShowSuccess(false), 2000);
        return () => clearTimeout(timer);
      }
    }, [successState, loading]);

    React.useEffect(() => {
      if (errorState && !loading) {
        setShowError(true);
        const timer = setTimeout(() => setShowError(false), 2000);
        return () => clearTimeout(timer);
      }
    }, [errorState, loading]);

    const buttonClassName = cn(
      className,
      showSuccess && "micro-success",
      showError && "micro-error"
    );

    return (
      <Button
        ref={ref}
        disabled={disabled || loading}
        className={buttonClassName}
        {...props}
      >
        {loading && (
          <Spinner 
            size="sm" 
            className="mr-2 animate-spin" 
          />
        )}
        <span
          className={cn(
            "transition-opacity duration-fast",
            loading && "opacity-50"
          )}
        >
          {loading && loadingText ? loadingText : children}
        </span>
      </Button>
    );
  }
);

LoadingButton.displayName = "LoadingButton";
